import { useContext } from 'react';
import { SimpleGrid, Button } from '@chakra-ui/react';
import {
  getButtonValues,
  getEditedRoundHistory,
  updateInput,
} from '../../helpers/utils/helperFunctions';
import { GlobalContext } from '../../helpers/context/GlobalContext';
import type { Round, InputValue } from '../../types';

export interface ButtonGridProps {
  type: 'Bid' | 'Actual';
  fieldToUpdate: string;
  currentRound: Round;
  index?: number | string;
  roundHistory?: Round[];
  isCurrent: boolean;
  setIsModalOpen?: (isOpen: boolean) => void;
  onCustomUpdate?: (input: InputValue) => void;
}

function ButtonGrid({
  type,
  fieldToUpdate,
  currentRound,
  index,
  roundHistory,
  isCurrent,
  setIsModalOpen,
  onCustomUpdate,
}: ButtonGridProps) {
  const { setCurrentRound, setRoundHistory } = useContext(GlobalContext);
  const buttonValues = getButtonValues(type);

  const onSelect = (input: InputValue) => {
    if (setIsModalOpen) {
      setIsModalOpen(false);
    }

    // If there's a custom update handler, use it instead of the default behavior
    if (onCustomUpdate) {
      onCustomUpdate(input);
      return;
    }

    if (isCurrent) {
      setCurrentRound({
        input,
        fieldToUpdate,
        currentRound: { ...currentRound },
      });
    } else {
      let updatedRound = updateInput({ input, fieldToUpdate, currentRound });

      // If this is an individual actual update, mark it as manually entered
      if (fieldToUpdate && fieldToUpdate.includes('Actual')) {
        const teamNumber = fieldToUpdate.includes('team1') ? 1 : 2;
        const playerNumber = fieldToUpdate.includes('p1Actual') ? 'P1' : 'P2';
        const autoGeneratedKey = `team${teamNumber}${playerNumber}`;

        updatedRound = {
          ...updatedRound,
          autoGeneratedActuals: {
            ...updatedRound.autoGeneratedActuals,
            [autoGeneratedKey]: false,
          } as Round['autoGeneratedActuals'],
        };
      }

      const newRoundHistory = getEditedRoundHistory({
        index: index as number,
        updatedRound,
        roundHistory: roundHistory || [],
      });
      setRoundHistory([...newRoundHistory]);
    }
  };

  return (
    <>
      <SimpleGrid columns={3} gap={4}>
        {buttonValues.map((buttonVal, i) => {
          return (
            <Button
              key={i}
              onClick={() => {
                onSelect(buttonVal);
              }}
              data-cy={`${type.toLowerCase()}SelectionButton`}
              variant="outline"
              whiteSpace="normal"
              h="auto"
              minH="40px"
              py={2}
            >
              {buttonVal}
            </Button>
          );
        })}
      </SimpleGrid>
    </>
  );
}

export default ButtonGrid;
